import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ComparisonHeader from './components/ComparisonHeader';
import ComparisonTable from './components/ComparisonTable';
import ComparisonSummary from './components/ComparisonSummary';
import MobileComparisonCards from './components/MobileComparisonCards';
import { ComparisonTableSkeleton, MobileComparisonSkeleton } from './components/ComparisonSkeleton';

const PropertyComparison = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [properties, setProperties] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoading, setIsLoading] = useState(true);

  const mockUser = {
    id: 1,
    name: 'Sarah Mitchell',
    email: 'sarah.mitchell@email.com',
    role: 'user'
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    // Small delay to show skeleton, then load properties
    const timer = setTimeout(() => {
      const stateProps = location?.state?.properties;
      if (stateProps?.length >= 2) {
        setProperties(stateProps);
        setIsLoading(false);
        return;
      }
      try {
        const stored = JSON.parse(localStorage.getItem('listinglens_compare_properties') || '[]');
        if (stored?.length >= 2) {
          setProperties(stored);
        } else {
          navigate('/search-results');
        }
      } catch {
        navigate('/search-results');
      }
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [location, navigate]);

  const handleRemoveProperty = (id) => {
    const updated = properties?.filter(p => p?.id !== id);
    setProperties(updated);
    localStorage.setItem('listinglens_compare_properties', JSON.stringify(updated));
    if (updated?.length < 2) {
      navigate('/search-results');
    }
  };

  const handleClearAll = () => {
    localStorage.removeItem('listinglens_compare_properties');
    navigate('/search-results');
  };

  const handleLogout = () => navigate('/authentication');

  return (
    <>
      <Helmet>
        <title>Compare Properties - ListingLens</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A1628' }}>
        <Header user={mockUser} onLogout={handleLogout} />

        {isLoading ? (
          <>
            {/* Skeleton header */}
            <div
              className="px-4 md:px-6 lg:px-8 py-4"
              style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.1)' }}
            >
              <div
                className="h-7 w-64 rounded-lg mb-2"
                style={{
                  background: 'linear-gradient(90deg, rgba(0,212,255,0.04) 25%, rgba(0,212,255,0.1) 50%, rgba(0,212,255,0.04) 75%)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 1.5s infinite'
                }}
              />
              <div
                className="h-4 w-48 rounded-lg"
                style={{
                  background: 'linear-gradient(90deg, rgba(0,212,255,0.04) 25%, rgba(0,212,255,0.1) 50%, rgba(0,212,255,0.04) 75%)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 1.5s infinite'
                }}
              />
            </div>
            <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
              {!isMobile ? <ComparisonTableSkeleton /> : <MobileComparisonSkeleton />}
            </main>
          </>
        ) : properties?.length < 2 ? (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#00D4FF' }} />
              <p style={{ color: '#6B9EC4' }}>Loading comparison...</p>
            </div>
          </div>
        ) : (
          <>
            <ComparisonHeader
              propertyCount={properties?.length}
              onClearAll={handleClearAll}
            />
            <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
              {/* Add more properties prompt if < 4 */}
              {properties?.length < 4 && (
                <div
                  className="flex items-center justify-between px-4 py-3 rounded-xl mb-6"
                  style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.12)' }}
                >
                  <div className="flex items-center gap-2">
                    <Icon name="Info" size={16} color="#00D4FF" />
                    <span className="text-sm" style={{ color: '#8BA5C0' }}>
                      You can compare up to 4 properties. Currently comparing {properties?.length}.
                    </span>
                  </div>
                  <button
                    onClick={() => navigate('/search-results')}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                    style={{ background: 'rgba(0, 212, 255, 0.1)', color: '#00D4FF', border: '1px solid rgba(0, 212, 255, 0.2)' }}
                  >
                    <Icon name="Plus" size={14} color="currentColor" />
                    Add Property
                  </button>
                </div>
              )}

              {/* Desktop: comparison table */}
              {!isMobile ? (
                <div
                  className="rounded-2xl overflow-hidden"
                  style={{ border: '1px solid rgba(0, 212, 255, 0.15)' }}
                >
                  <ComparisonTable
                    properties={properties}
                    onRemove={handleRemoveProperty}
                  />
                </div>
              ) : (
                <MobileComparisonCards
                  properties={properties}
                  onRemove={handleRemoveProperty}
                />
              )}

              <ComparisonSummary properties={properties} />
            </main>
          </>
        )}
      </div>
    </>
  );
};

export default PropertyComparison;
